import React, { useEffect, useRef, useState } from 'react';

/**
 * اینپوت خودرشد فرمول‌ها.
 *
 * نکته حیاتی: تایپ روی «state محلی» انجام می‌شود و فقط با blur یا Enter
 * به سند (updateAttributes) کامیت می‌گردد. اگر هر keystroke مستقیم به سند
 * برود، ProseMirror نود را دوباره رندر می‌کند، input بازسازی و فوکوس/کرسر
 * می‌پرد (به‌خصوص وقتی فرمول وسط متن RTL باشد).
 *
 * - عرض = دقیقاً عرض متن؛ خالی → مربع خط‌چین هم‌اندازه یک کاراکتر
 * - dir=ltr برای تایپ ریاضی چپ‌به‌راست داخل متن راست‌چین
 * - fillWidth=true → ۱۰۰٪ والد (اسلات‌های کسر)
 */
const AutoGrowInput = ({ value, onCommit, onDraftChange, fillWidth = false, className = '', ...rest }) => {
  const measureRef = useRef(null);
  const inputRef = useRef(null);
  const [draft, setDraft] = useState(value);
  const [width, setWidth] = useState(undefined);

  // همگام‌سازی با تغییر بیرونی (undo / لود سوال)
  useEffect(() => setDraft(value), [value]);

  // عرض خودرشد (فقط حالت عادی؛ در fillWidth والد عرض را می‌دهد)
  useEffect(() => {
    if (!fillWidth && measureRef.current) {
      const textW = measureRef.current.offsetWidth;
      setWidth(Math.max(textW, Math.round(parseFloat(getComputedStyle(measureRef.current).fontSize) * 0.75)));
    }
  }, [draft, fillWidth]);

  const commit = () => {
    if (draft !== value && onCommit) onCommit(draft);
  };

  return (
    <span
      style={{
        position: 'relative',
        display: 'inline-flex',
        ...(fillWidth ? { width: '100%', justifyContent: 'center' } : {}),
      }}
    >
      {!fillWidth && (
        <span
          ref={measureRef}
          aria-hidden="true"
          style={{ position: 'absolute', visibility: 'hidden', whiteSpace: 'pre', left: 0, top: 0 }}
        >
          {draft || '?'}
        </span>
      )}
      {draft === '' && (
        <span
          aria-hidden="true"
          onClick={() => inputRef.current?.focus()}
          style={{
            position: 'absolute',
            width: '0.6em',
            height: '0.6em',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -55%)',
            border: '1.5px dashed #94a3b8',
            borderRadius: '2px',
            pointerEvents: 'none',
          }}
        />
      )}
      <input
        {...rest}
        dir="ltr"
        ref={(el) => {
          inputRef.current = el;
          if (typeof rest.ref === 'function') rest.ref(el);
        }}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          // اطلاع زنده تغییر draft به والد — برای محاسبه لحظه‌ای عرض ستون کسر
          if (onDraftChange) onDraftChange(e.target.value);
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
            inputRef.current?.blur();
          }
          rest.onKeyDown && rest.onKeyDown(e);
        }}
        style={{
          direction: 'ltr',
          textAlign: 'center',
          ...(!fillWidth && width ? { width: `${width}px` } : {}),
          ...(fillWidth ? { width: '100%' } : {}),
          ...rest.style,
        }}
        className={`${className} bg-transparent outline-none text-center`}
      />
    </span>
  );
};

export default AutoGrowInput;
